import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { getValuationTypes } from '../../api/agenda';
import type { ValuationType } from '../../types/Agenda';
import type { DayOpenRunParams } from '../../types/DayOpen';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import './ValuationParamsModal.css';
import { LopDateInput } from '../../components/LopDateInput';

// Most recent TASE trading day (Mon-Fri, Israel works Mon-Fri). Used as the
// default valuation date; the operator can override (e.g. two days back over a
// holiday).
function lastTradingDayISO(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = d.getDay(); // 0=Sun..6=Sat
  if (dow === 0) d.setDate(d.getDate() - 2); // Sunday -> Friday
  else if (dow === 6) d.setDate(d.getDate() - 1); // Saturday -> Friday
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface ValuationParamsModalProps {
  title: string;
  initial?: Partial<DayOpenRunParams>;
  onConfirm: (params: DayOpenRunParams) => void;
  onClose: () => void;
}

export function ValuationParamsModal({ title, initial, onConfirm, onClose }: ValuationParamsModalProps) {
  const [types, setTypes] = useState<ValuationType[] | null>(null);
  const [typesError, setTypesError] = useState<string | null>(null);

  const fallbackDate = useMemo(() => lastTradingDayISO(), []);
  const [israeliDate, setIsraeliDate] = useState(initial?.israeliValuationDate ?? fallbackDate);
  const [foreignDate, setForeignDate] = useState(initial?.foreignValuationDate ?? fallbackDate);
  const [israeliType, setIsraeliType] = useState<number | ''>(initial?.israeliValuationType ?? '');
  const [foreignType, setForeignType] = useState<number | ''>(initial?.foreignValuationType ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getValuationTypes()
      .then((data) => {
        if (cancelled) return;
        const active = data.filter((x) => x.isActive);
        setTypes(active);
        // Default both types to the first active one (usually "regular").
        const def = active[0]?.valuationTypeId;
        if (def !== undefined) {
          setIsraeliType((p) => (p === '' ? def : p));
          setForeignType((p) => (p === '' ? def : p));
        }
      })
      .catch((e) => {
        if (!cancelled) setTypesError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const confirm = () => {
    if (!israeliDate || !foreignDate || israeliType === '' || foreignType === '') {
      setError(t('dayOpen.params.validation'));
      return;
    }
    onConfirm({
      israeliValuationDate: israeliDate,
      israeliValuationType: israeliType,
      foreignValuationDate: foreignDate,
      foreignValuationType: foreignType,
    });
  };

  return (
    <div
      className="vparams__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="vparams" role="dialog" aria-modal="true" aria-label={title}>
        <header className="vparams__header">
          <h2 className="vparams__title">{title}</h2>
          <button type="button" className="vparams__close" aria-label={t('recordForm.close')} onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        {typesError && <div className="vparams__error">{typesError}</div>}

        <div className="vparams__grid">
          <div className="vparams__section">
            <h3 className="vparams__section-title">{t('dayOpen.params.israeli')}</h3>
            <label className="vparams__field">
              <span>{t('dayOpen.params.valuationDate')}</span>
              <LopDateInput value={israeliDate} onChange={setIsraeliDate} />
            </label>
            <label className="vparams__field">
              <span>{t('dayOpen.params.valuationType')}</span>
              <select
                value={israeliType}
                onChange={(e) => setIsraeliType(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">—</option>
                {(types ?? []).map((x) => (
                  <option key={x.valuationTypeId} value={x.valuationTypeId}>
                    {x.valuationTypeName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="vparams__section">
            <h3 className="vparams__section-title">{t('dayOpen.params.foreign')}</h3>
            <label className="vparams__field">
              <span>{t('dayOpen.params.valuationDate')}</span>
              <LopDateInput value={foreignDate} onChange={setForeignDate} />
            </label>
            <label className="vparams__field">
              <span>{t('dayOpen.params.valuationType')}</span>
              <select
                value={foreignType}
                onChange={(e) => setForeignType(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">—</option>
                {(types ?? []).map((x) => (
                  <option key={x.valuationTypeId} value={x.valuationTypeId}>
                    {x.valuationTypeName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && <div className="vparams__error">{error}</div>}

        <footer className="vparams__footer">
          <LopButton variant="primary" onClick={confirm}>
            {t('dayOpen.params.confirm')}
          </LopButton>
          <LopButton variant="secondary" onClick={onClose}>
            {t('recordForm.cancel')}
          </LopButton>
        </footer>
      </div>
    </div>
  );
}
